import { transformMetadataScopesResponse } from './metadataTransforms';
import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '@/api/api';
import { ENDPOINT } from '@/constants/endpoints';
import {
  MetadataCategory,
  MetadataDepartmentsResponse,
  MetaDataHrisIntegrationsItem,
  MetadataLeadersResponse,
  MetadataScopesPayload,
  MetadataScopesResponse,
} from '@/types/metadata';

type MetadataLeadersPayload = {
  search?: string;
  cursor?: string;
  limit?: number;
  period?: string;
};

type MetadataLeadersInfiniteQueryArg = {
  search?: string;
  limit?: number;
  period?: string;
};

type MetadataDepartmentsPayload = {
  period: string;
  offset?: number;
  limit?: number;
};

export const metadataApi = createApi({
  reducerPath: 'metadataApi',
  baseQuery: axiosBaseQuery,
  endpoints: builder => ({
    getMetadataLeaders: builder.query<MetadataLeadersResponse, MetadataLeadersPayload>({
      query: params => ({
        url: ENDPOINT.METADATA.GET_LEADERS(),
        method: 'GET',
        params,
      }),
    }),
    getMetadataLeadersPaginated: builder.infiniteQuery<
      MetadataLeadersResponse,
      MetadataLeadersInfiniteQueryArg,
      string | undefined
    >({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: lastPage => lastPage.nextCursor || undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: ENDPOINT.METADATA.GET_LEADERS(),
        method: 'GET',
        params: {
          search: queryArg.search,
          limit: queryArg.limit,
          period: queryArg.period,
          cursor: pageParam,
        },
      }),
    }),
    getMetadataCategories: builder.query<MetadataCategory[], void>({
      query: () => ({
        url: ENDPOINT.METADATA.GET_CATEGORIES(),
        method: 'GET',
      }),
    }),
    getMetadataDepartments: builder.query<MetadataDepartmentsResponse, MetadataDepartmentsPayload>({
      query: params => ({
        url: ENDPOINT.METADATA.GET_DEPARTMENTS(),
        method: 'GET',
        params,
      }),
    }),
    getMetadataScopes: builder.query<MetadataScopesResponse, MetadataScopesPayload>({
      query: params => ({
        url: ENDPOINT.METADATA.GET_SCOPES(),
        method: 'GET',
        params,
      }),
      transformResponse: transformMetadataScopesResponse,
    }),
    getMetadataHrisIntegrations: builder.query<MetaDataHrisIntegrationsItem[], void>({
      query: () => ({
        url: ENDPOINT.METADATA.GET_HRIS_INTEGRATIONS(),
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetMetadataLeadersQuery,
  useGetMetadataLeadersPaginatedInfiniteQuery,
  useGetMetadataCategoriesQuery,
  useGetMetadataDepartmentsQuery,
  useGetMetadataScopesQuery,
  useGetMetadataHrisIntegrationsQuery,
} = metadataApi;
